/**
 * Bentuk cluster di atas crossplot: lingkaran, elips, kotak, dan jejak bebas.
 *
 * Semuanya dibangkitkan sebagai poligon dalam pecahan kotak gambar (`Vertex`),
 * sebab itulah satu-satunya bentuk yang dikenal seluruh jalur di belakangnya:
 * penghitung seleksi di layar, penyimpanan kelas dalam nilai kurva, dan
 * `crossplot.classify` di server yang menerbitkan kurva kelas. Lingkaran yang
 * disimpan sebagai pusat dan jari-jari akan butuh penggolong kedua di tiap
 * tempat itu — dan dua penggolong adalah dua jawaban untuk satu titik.
 *
 * Empat puluh delapan simpul untuk yang melengkung: selisihnya dari lengkung
 * sejati di bawah seperseratus jari-jari, lebih halus daripada satu titik awan.
 */

import type { Vertex } from "./xplot";

export type ShapeTool = "polygon" | "circle" | "ellipse" | "rect" | "freehand";

export const ROUND_VERTICES = 48;

/** Kotak dari dua sudut yang berseberangan. */
export function rectShape(a: Vertex, b: Vertex): Vertex[] {
  return [
    { x: a.x, y: a.y },
    { x: b.x, y: a.y },
    { x: b.x, y: b.y },
    { x: a.x, y: b.y },
  ];
}

function ring(cx: number, cy: number, rx: number, ry: number, n: number): Vertex[] {
  return Array.from({ length: n }, (_, i) => {
    const t = (i / n) * 2 * Math.PI;
    return { x: cx + rx * Math.cos(t), y: cy + ry * Math.sin(t) };
  });
}

/** Elips yang tertulis di dalam kotak yang diseret dari `a` ke `b`. */
export function ellipseShape(a: Vertex, b: Vertex, n = ROUND_VERTICES): Vertex[] {
  return ring((a.x + b.x) / 2, (a.y + b.y) / 2, Math.abs(b.x - a.x) / 2, Math.abs(b.y - a.y) / 2, n);
}

/**
 * Lingkaran berpusat di `center` yang tepinya melewati `edge` — bulat di layar.
 *
 * Kotak gambar direntangkan (`preserveAspectRatio="none"`), jadi satu pecahan
 * mendatar dan satu pecahan tegak adalah dua panjang piksel yang berbeda.
 * Jari-jarinya dihitung dalam piksel lalu dibagi dengan lebar dan tinggi
 * kotaknya masing-masing; tanpa itu yang tergambar lonjong.
 */
export function circleShape(
  center: Vertex,
  edge: Vertex,
  box: { width: number; height: number },
  n = ROUND_VERTICES,
): Vertex[] {
  const r = Math.hypot((edge.x - center.x) * box.width, (edge.y - center.y) * box.height);
  return ring(center.x, center.y, r / box.width, r / box.height, n);
}

/**
 * Jejak tangan bebas, dijarangkan: titik yang lebih dekat dari `minPx` ke
 * titik yang disimpan sebelumnya dibuang. Gerak tetikus melahirkan ratusan
 * titik per detik, dan poligon beratus simpul hanya memberatkan penggolong
 * tanpa menambah satu pun batas yang bisa dilihat.
 */
export function thinPath(
  points: Vertex[],
  box: { width: number; height: number },
  minPx = 6,
): Vertex[] {
  const out: Vertex[] = [];
  for (const p of points) {
    const last = out[out.length - 1];
    if (!last || Math.hypot((p.x - last.x) * box.width, (p.y - last.y) * box.height) >= minPx) {
      out.push(p);
    }
  }
  return out;
}

/** Bentuk sebuah alat seret dari titik awal dan titik akhirnya; `null` bila terlalu kecil. */
export function dragShape(
  tool: Exclude<ShapeTool, "polygon" | "freehand">,
  start: Vertex,
  end: Vertex,
  box: { width: number; height: number },
  minPx = 4,
): Vertex[] | null {
  const w = Math.abs(end.x - start.x) * box.width;
  const h = Math.abs(end.y - start.y) * box.height;
  if (tool === "circle" ? Math.hypot(w, h) < minPx : w < minPx || h < minPx) return null;
  if (tool === "rect") return rectShape(start, end);
  if (tool === "ellipse") return ellipseShape(start, end);
  return circleShape(start, end, box);
}

export type Handle = { kind: "vertex"; index: number } | { kind: "mid"; index: number };

/**
 * Pegangan terdekat dalam `radiusPx` piksel: sebuah simpul, atau titik tengah
 * sebuah sisi (menyeretnya menyisipkan simpul baru setelah `index`). Simpul
 * didahulukan atas titik tengah bila keduanya dalam jangkauan.
 */
export function nearestHandle(
  polygon: Vertex[],
  at: Vertex,
  box: { width: number; height: number },
  radiusPx = 8,
): Handle | null {
  const gap = (p: Vertex) => Math.hypot((p.x - at.x) * box.width, (p.y - at.y) * box.height);
  let best: Handle | null = null;
  let nearest = radiusPx;
  polygon.forEach((v, index) => {
    const d = gap(v);
    if (d <= nearest) {
      nearest = d;
      best = { kind: "vertex", index };
    }
  });
  if (best) return best;
  nearest = radiusPx;
  midpoints(polygon).forEach((m, index) => {
    const d = gap(m);
    if (d <= nearest) {
      nearest = d;
      best = { kind: "mid", index };
    }
  });
  return best;
}

/** Titik tengah tiap sisi, termasuk sisi penutup dari simpul terakhir ke yang pertama. */
export function midpoints(polygon: Vertex[]): Vertex[] {
  if (polygon.length < 3) return [];
  return polygon.map((v, i) => {
    const next = polygon[(i + 1) % polygon.length];
    return { x: (v.x + next.x) / 2, y: (v.y + next.y) / 2 };
  });
}

export function moveVertex(polygon: Vertex[], index: number, to: Vertex): Vertex[] {
  return polygon.map((v, i) => (i === index ? { x: clamp01(to.x), y: clamp01(to.y) } : v));
}

export function insertVertex(polygon: Vertex[], after: number, at: Vertex): Vertex[] {
  return [...polygon.slice(0, after + 1), { x: clamp01(at.x), y: clamp01(at.y) }, ...polygon.slice(after + 1)];
}

/** Menghapus simpul; poligon tidak pernah dibiarkan kurang dari tiga simpul. */
export function removeVertex(polygon: Vertex[], index: number): Vertex[] {
  return polygon.length > 3 ? polygon.filter((_, i) => i !== index) : polygon;
}

function clamp01(v: number): number {
  return Math.min(1, Math.max(0, v));
}
